import { del, get, post, put } from './api';
import type {
  AdminFeatureFlag,
  FeatureFlagAllowlistUser,
  FeatureFlagUserSearchResult,
  MyFeatureFlagsResponse,
  UpdateFeatureFlagRequest,
} from '../types/featureFlags';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const nullableStr = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

const normalizeMyFlags = (raw: unknown): MyFeatureFlagsResponse => {
  const enabled = isRecord(raw) && Array.isArray(raw.enabled) ? raw.enabled : [];
  return { enabled: enabled.filter((key): key is string => typeof key === 'string' && key.length > 0) };
};

const normalizeAdminFlag = (raw: unknown): AdminFeatureFlag | null => {
  if (!isRecord(raw)) return null;
  const key = nullableStr(raw.key);
  if (!key) return null;
  return {
    key,
    name: nullableStr(raw.name) ?? key,
    description: typeof raw.description === 'string' ? raw.description : '',
    // A flag the server did not describe as on is treated as off: never show a feature by accident.
    enabled: raw.enabled === true,
    isDefault: raw.isDefault === true,
    updatedAt: nullableStr(raw.updatedAt),
    updatedBy: nullableStr(raw.updatedBy),
    updatedByName: nullableStr(raw.updatedByName),
    allowlistCount:
      typeof raw.allowlistCount === 'number' && Number.isFinite(raw.allowlistCount) && raw.allowlistCount > 0
        ? Math.floor(raw.allowlistCount)
        : 0,
    organizationCount:
      typeof raw.organizationCount === 'number' &&
      Number.isFinite(raw.organizationCount) &&
      raw.organizationCount > 0
        ? Math.floor(raw.organizationCount)
        : 0,
  };
};

const normalizeSearchResult = (raw: unknown): FeatureFlagUserSearchResult | null => {
  if (!isRecord(raw)) return null;
  const userId = nullableStr(raw.userId);
  if (!userId) return null;
  const email = typeof raw.email === 'string' ? raw.email : '';
  return {
    userId,
    name: nullableStr(raw.name) ?? (email || userId),
    email,
    role: typeof raw.role === 'string' ? raw.role : '',
    organizationId: typeof raw.organizationId === 'string' ? raw.organizationId : '',
    organizationName: typeof raw.organizationName === 'string' ? raw.organizationName : '',
  };
};

const normalizeAllowlistUser = (raw: unknown): FeatureFlagAllowlistUser | null => {
  const base = normalizeSearchResult(raw);
  if (!base || !isRecord(raw)) return null;
  return { ...base, addedAt: nullableStr(raw.addedAt), addedByName: nullableStr(raw.addedByName) };
};

/**
 * The list and add calls answer with the whole allowlist. A body that is not a list throws rather
 * than reading as "nobody", which would show an empty list after a save that worked.
 */
const normalizeAllowlist = (raw: unknown): FeatureFlagAllowlistUser[] => {
  if (!Array.isArray(raw)) throw new Error('The server did not return the list of users.');
  return raw.map(normalizeAllowlistUser).filter((user): user is FeatureFlagAllowlistUser => user !== null);
};

const allowlistPath = (key: string) => `/api/admin/feature-flags/${encodeURIComponent(key)}/users`;

/** The flags that are on for the signed-in user. Throws `ApiError`; callers fail closed. */
export const getMyFeatureFlags = async (signal?: AbortSignal): Promise<MyFeatureFlagsResponse> =>
  normalizeMyFlags(await get<unknown>('/api/feature-flags/me', { signal }));

/** Every flag with its state. System admin only. Throws `ApiError`. */
export const getAdminFeatureFlags = async (signal?: AbortSignal): Promise<AdminFeatureFlag[]> => {
  const raw = await get<unknown>('/api/admin/feature-flags', { signal });
  return (Array.isArray(raw) ? raw : [])
    .map(normalizeAdminFlag)
    .filter((flag): flag is AdminFeatureFlag => flag !== null);
};

/** Turns one flag on or off and answers with what the server kept. Throws `ApiError`. */
export const updateFeatureFlag = async (
  key: string,
  enabled: boolean,
  signal?: AbortSignal
): Promise<AdminFeatureFlag> => {
  const body: UpdateFeatureFlagRequest = { enabled };
  const flag = normalizeAdminFlag(
    await put<unknown>(`/api/admin/feature-flags/${encodeURIComponent(key)}`, body, { signal })
  );
  if (!flag) throw new Error('The server did not return the saved flag.');
  return flag;
};

/** The users a flag is on for while it is off for everyone else. System admin only. Throws `ApiError`. */
export const getFeatureFlagUsers = async (
  key: string,
  signal?: AbortSignal
): Promise<FeatureFlagAllowlistUser[]> => normalizeAllowlist(await get<unknown>(allowlistPath(key), { signal }));

/**
 * Puts users on a flag's allowlist and answers with the whole list. Adding someone already on it
 * changes nothing. Throws `ApiError` (400 when a user is not found, and then nobody is added).
 */
export const addFeatureFlagUsers = async (
  key: string,
  userIds: string[],
  signal?: AbortSignal
): Promise<FeatureFlagAllowlistUser[]> =>
  normalizeAllowlist(await post<unknown>(allowlistPath(key), { userIds }, { signal }));

/** Takes one user off a flag's allowlist. Throws `ApiError`. */
export const removeFeatureFlagUser = async (key: string, userId: string, signal?: AbortSignal): Promise<void> => {
  await del<unknown>(`${allowlistPath(key)}/${encodeURIComponent(userId)}`, { signal });
};

/** Users in every organization whose name or email contains `query` (2 characters or more). Throws `ApiError`. */
export const searchFeatureFlagUsers = async (
  query: string,
  signal?: AbortSignal
): Promise<FeatureFlagUserSearchResult[]> => {
  const raw = await get<unknown>(
    `/api/admin/feature-flags/user-search?q=${encodeURIComponent(query)}&limit=20`,
    { signal }
  );
  return (Array.isArray(raw) ? raw : [])
    .map(normalizeSearchResult)
    .filter((user): user is FeatureFlagUserSearchResult => user !== null);
};
